import { useEffect, useState } from 'react'
import { getConteudoChave, putConteudoChave, CONTEUDO_CHAVES } from '@/api/adminApi'
import { isConteudoFlagEnabled } from '@/utils/featureFlags'
import { resolveAgendamentoFlagsFromRaw } from '@/utils/agendamentoFlags'
import { notifyConteudoUpdated } from '@/utils/conteudoSync'
import { AlertBanner } from '@/components/AlertBanner'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './AdminVisibilidadePage.module.css'
import adminStyles from './Admin.module.css'

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
}) {
  return (
    <label className={styles.toggle} aria-label={label}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className={styles.toggleTrack}>
        <span className={styles.toggleThumb} />
      </span>
    </label>
  )
}

function FlagRow({
  title,
  description,
  checked,
  onChange,
}: {
  title: string
  description?: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className={styles.flagRow}>
      <div className={styles.flagText}>
        <span className={styles.flagTitle}>{title}</span>
        {description && <span className={styles.flagDesc}>{description}</span>}
      </div>
      <Toggle checked={checked} onChange={onChange} label={title} />
    </div>
  )
}

function Section({
  icon,
  title,
  description,
  children,
}: {
  icon: string
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionIcon} aria-hidden>
          {icon}
        </span>
        <div>
          <h3 className={styles.sectionTitle}>{title}</h3>
          {description && <p className={styles.sectionDesc}>{description}</p>}
        </div>
      </div>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  )
}

export function AdminVisibilidadePage() {
  const [agendamentoFluxo, setAgendamentoFluxo] = useState(true)
  const [historicoCliente, setHistoricoCliente] = useState(true)
  const [servicosMarcacao, setServicosMarcacao] = useState(true)
  const [notificacoesEnabled, setNotificacoesEnabled] = useState(true)
  const [pacotesEnabled, setPacotesEnabled] = useState(true)
  const [tarefasEnabled, setTarefasEnabled] = useState(true)
  const [vipEnabled, setVipEnabled] = useState(true)
  const [quickActionsEnabled, setQuickActionsEnabled] = useState(true)
  const [showQuemSomos, setShowQuemSomos] = useState(true)
  const [showServicos, setShowServicos] = useState(true)
  const [showContatos, setShowContatos] = useState(true)
  const [showMapa, setShowMapa] = useState(true)
  const [showGaleria, setShowGaleria] = useState(true)
  const [showSocial, setShowSocial] = useState(true)
  const [showMinhaConta, setShowMinhaConta] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const { notifySaved, notifyError } = useSaveFeedback()

  useEffect(() => {
    let cancelled = false
    async function load() {
      setError(null)
      try {
        const values = await Promise.all([
          getConteudoChave(CONTEUDO_CHAVES.agendamento_fluxo_enabled).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.historico_cliente_enabled).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.servicos_marcacao_enabled).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.agendamentos_enabled).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.notificacoes_enabled).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.pacotes_enabled).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.tarefas_enabled).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.vip_enabled).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.login_quick_actions_enabled).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.menu_show_quem_somos).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.menu_show_servicos).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.menu_show_contatos).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.menu_show_mapa).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.menu_show_galeria).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.menu_show_social).catch(() => null),
          getConteudoChave(CONTEUDO_CHAVES.menu_show_minha_conta).catch(() => null),
        ])
        if (cancelled) return
        const [
          agFluxo,
          agHist,
          agServ,
          agLegacy,
          notif,
          pacotes,
          tarefas,
          vip,
          quickActions,
          qSomos,
          qServicos,
          qContatos,
          qMapa,
          qGaleria,
          qSocial,
          qConta,
        ] = values
        const agFlags = resolveAgendamentoFlagsFromRaw(agFluxo, agHist, agServ, agLegacy)
        setAgendamentoFluxo(agFlags.fluxo)
        setHistoricoCliente(agFlags.historico)
        setServicosMarcacao(agFlags.servicosMarcacao)
        setNotificacoesEnabled(isConteudoFlagEnabled(notif))
        setPacotesEnabled(isConteudoFlagEnabled(pacotes))
        setTarefasEnabled(isConteudoFlagEnabled(tarefas))
        setVipEnabled(isConteudoFlagEnabled(vip))
        setQuickActionsEnabled(isConteudoFlagEnabled(quickActions))
        setShowQuemSomos(isConteudoFlagEnabled(qSomos))
        setShowServicos(isConteudoFlagEnabled(qServicos))
        setShowContatos(isConteudoFlagEnabled(qContatos))
        setShowMapa(isConteudoFlagEnabled(qMapa))
        setShowGaleria(isConteudoFlagEnabled(qGaleria))
        setShowSocial(isConteudoFlagEnabled(qSocial))
        setShowMinhaConta(isConteudoFlagEnabled(qConta))
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Erro ao carregar')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const flag = (on: boolean) => (on ? '1' : '0')

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const putSilent = { notify: false as const }
      await Promise.all([
        putConteudoChave(CONTEUDO_CHAVES.agendamento_fluxo_enabled, flag(agendamentoFluxo), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.historico_cliente_enabled, flag(historicoCliente), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.servicos_marcacao_enabled, flag(servicosMarcacao), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.notificacoes_enabled, flag(notificacoesEnabled), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.pacotes_enabled, flag(pacotesEnabled), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.tarefas_enabled, flag(tarefasEnabled), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.vip_enabled, flag(vipEnabled), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.login_quick_actions_enabled, flag(quickActionsEnabled), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.menu_show_quem_somos, flag(showQuemSomos), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.menu_show_servicos, flag(showServicos), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.menu_show_contatos, flag(showContatos), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.menu_show_mapa, flag(showMapa), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.menu_show_galeria, flag(showGaleria), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.menu_show_social, flag(showSocial), putSilent),
        putConteudoChave(CONTEUDO_CHAVES.menu_show_minha_conta, flag(showMinhaConta), putSilent),
      ])
      notifyConteudoUpdated({
        [CONTEUDO_CHAVES.agendamento_fluxo_enabled]: flag(agendamentoFluxo),
        [CONTEUDO_CHAVES.historico_cliente_enabled]: flag(historicoCliente),
        [CONTEUDO_CHAVES.servicos_marcacao_enabled]: flag(servicosMarcacao),
        [CONTEUDO_CHAVES.notificacoes_enabled]: flag(notificacoesEnabled),
        [CONTEUDO_CHAVES.pacotes_enabled]: flag(pacotesEnabled),
        [CONTEUDO_CHAVES.tarefas_enabled]: flag(tarefasEnabled),
        [CONTEUDO_CHAVES.vip_enabled]: flag(vipEnabled),
        [CONTEUDO_CHAVES.login_quick_actions_enabled]: flag(quickActionsEnabled),
        [CONTEUDO_CHAVES.menu_show_quem_somos]: flag(showQuemSomos),
        [CONTEUDO_CHAVES.menu_show_servicos]: flag(showServicos),
        [CONTEUDO_CHAVES.menu_show_contatos]: flag(showContatos),
        [CONTEUDO_CHAVES.menu_show_mapa]: flag(showMapa),
        [CONTEUDO_CHAVES.menu_show_galeria]: flag(showGaleria),
        [CONTEUDO_CHAVES.menu_show_social]: flag(showSocial),
        [CONTEUDO_CHAVES.menu_show_minha_conta]: flag(showMinhaConta),
      })
      setSuccess('Configurações guardadas com sucesso.')
      notifySaved('Configurações guardadas com sucesso.')
    } catch (e) {
      notifyError(e, 'Erro ao salvar')
      setError(e instanceof Error ? e.message : 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const menuItems: Array<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = [
    { label: 'Quem somos', checked: showQuemSomos, onChange: setShowQuemSomos },
    { label: 'Serviços', checked: showServicos, onChange: setShowServicos },
    { label: 'Contatos', checked: showContatos, onChange: setShowContatos },
    { label: 'Mapa', checked: showMapa, onChange: setShowMapa },
    { label: 'Galeria', checked: showGaleria, onChange: setShowGaleria },
    { label: 'Comunidade', checked: showSocial, onChange: setShowSocial },
    { label: 'Minha conta', checked: showMinhaConta, onChange: setShowMinhaConta },
  ]

  if (loading) return <p className={adminStyles.placeholderMsg}>A carregar…</p>

  return (
    <>
      <header className={adminStyles.sectionHeader}>
        <h1 className={adminStyles.sectionTitle}>Visibilidade e fluxos</h1>
        <p className={adminStyles.sectionSubtitle}>
          Ative ou oculte áreas da app conforme o seu negócio. Alterações aplicam-se logo após guardar.
        </p>
      </header>
      {error && <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />}
      {success && (
        <AlertBanner variant="success" message={success} onDismiss={() => setSuccess(null)} />
      )}
      <div className={adminStyles.sectionCard}>
        <form onSubmit={handleSalvar}>
          <p className={styles.intro}>
            Cada interruptor controla uma função à parte. Itens desligados deixam de aparecer no menu, na barra
            inferior ou nas páginas do cliente.
          </p>

          <Section
            icon="📦"
            title="Módulos principais"
            description="Funcionalidades centrais da experiência do cliente."
          >
            <div className={styles.agendaGroup}>
              <p className={styles.agendaGroupLabel}>Agendamentos</p>
              <FlagRow
                title="Agendar"
                description="Botão na barra inferior e fluxo de nova marcação."
                checked={agendamentoFluxo}
                onChange={setAgendamentoFluxo}
              />
              <FlagRow
                title="Histórico"
                description="Lista de agendamentos passados e futuros do cliente."
                checked={historicoCliente}
                onChange={setHistoricoCliente}
              />
              <FlagRow
                title="Marcação em Serviços"
                description="Toque num serviço abre o fluxo de agendamento."
                checked={servicosMarcacao}
                onChange={setServicosMarcacao}
              />
            </div>
            <FlagRow
              title="Notificações"
              description="Sino no topo e página de avisos."
              checked={notificacoesEnabled}
              onChange={setNotificacoesEnabled}
            />
            <FlagRow
              title="Pacotes"
              description="Módulo de pacotes na barra e páginas dedicadas."
              checked={pacotesEnabled}
              onChange={setPacotesEnabled}
            />
            <FlagRow
              title="Tarefas"
              description="Módulo de tarefas para admin e profissionais (não aparece para clientes)."
              checked={tarefasEnabled}
              onChange={setTarefasEnabled}
            />
            <FlagRow
              title="Programa V.I.P"
              description="Link em Serviços e página do programa."
              checked={vipEnabled}
              onChange={setVipEnabled}
            />
          </Section>

          <Section icon="🏠" title="Menu inicial" description="Entradas no ecrã principal após o login.">
            <div className={styles.menuGrid}>
              {menuItems.map((item) => (
                <div key={item.label} className={styles.menuItem}>
                  <span className={styles.menuLabel}>{item.label}</span>
                  <Toggle
                    checked={item.checked}
                    onChange={item.onChange}
                    label={`Mostrar ${item.label}`}
                  />
                </div>
              ))}
            </div>
          </Section>

          <Section icon="🔐" title="Ecrã de login">
            <FlagRow
              title="Atalhos abaixo de Entrar"
              description="Os 3 botões rápidos (criar conta, profissional, admin)."
              checked={quickActionsEnabled}
              onChange={setQuickActionsEnabled}
            />
          </Section>

          <div className={styles.actions}>
            <button type="submit" className={adminStyles.btnSalvar} disabled={saving}>
              {saving ? 'A guardar…' : 'Salvar alterações'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
